import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import { Collapse } from 'react-bootstrap';
import { Range } from 'rc-slider';
import { findIndex, findLastIndex, first, last } from 'lodash';

import masterField from './master-field';
import MoneyDisplay from '../money-display';
import NumberDisplay from '../number-display';
import AgeDisplay from '../age-display';
import MinMaxForm from './min-max-form';

import styles from './styles.css';

const MIN_MAX_CONTROL = 'minmax';

class SliderField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
    };
    this.onSliderChange = this.onSliderChange.bind(this);
    this.toggleExpand = this.toggleExpand.bind(this);
    this.forceCustom = this.forceCustom.bind(this);
  }

  onSliderChange(bounds) {
    const { path } = this.props;
    this.props.onUpdate(path, { values: [...bounds] });
  }

  getIsUsingMinMaxControl() {
    const { searchCriteria } = this.props;
    return !this.canUseSlider() || (searchCriteria && searchCriteria.control_type === MIN_MAX_CONTROL);
  }

  getMinMaxIndex(amountMin, amountMax) {
    const { searchCriteria } = this.props;
    const { filter } = searchCriteria;
    const tree = filter.values;
    // const minIndex = 0;
    const maxIndex = tree.length - 1;
    let minAmountIndex = findIndex(tree, t => t > amountMin);
    if (minAmountIndex < 0) {
      minAmountIndex = 0;
    } else if (minAmountIndex > 0) {
      minAmountIndex -= 1;
    }

    let maxAmountIndex = findLastIndex(tree, t => t < amountMax);
    if (maxAmountIndex < 0) {
      maxAmountIndex = maxIndex;
    } else if (maxAmountIndex < maxIndex) {
      maxAmountIndex += 1;
    }
    return [minAmountIndex, maxAmountIndex];
  }

  getMinMaxLabels() {
    const { searchCriteria, readOnly } = this.props;
    const { filter, values } = searchCriteria;
    const tree = filter.values;
    const amountMin = values[0];
    const amountMax = values[1];
    if (this.getIsUsingMinMaxControl()) {
      return this.makeLabels(amountMin, amountMax);
    }

    const [minAmountIndex, maxAmountIndex] = this.getMinMaxIndex(amountMin, amountMax);

    const minApproximateVal = tree[minAmountIndex];
    const maxApproximateVal = tree[maxAmountIndex];

    const labels = this.makeLabels(minApproximateVal, maxApproximateVal);

    return labels;
  }

  toggleExpand(e) {
    e.preventDefault();
    e.stopPropagation();
    const { disabled } = this.props;
    if (disabled) {
      return;
    }
    this.setState({ expanded: !this.state.expanded });
  }


  forceCustom(isCustom) {
    const { path, searchCriteria } = this.props;
    let controlType = MIN_MAX_CONTROL;
    if (!isCustom && this.canUseSlider()) {
      controlType = null;
    }
    if (isCustom && searchCriteria.control_type === MIN_MAX_CONTROL) {
      this.setState({ expanded: false });
    } else if (!isCustom && searchCriteria.control_type !== MIN_MAX_CONTROL) {
      this.setState({ expanded: false });
    }

    if (!isCustom && searchCriteria.control_type === MIN_MAX_CONTROL) {
      // if change from custom to slider, just select min max from tree
      const { filter } = searchCriteria;
      const tree = filter.values;
      this.props.onUpdate(path, { values: [first(tree), last(tree)], control_type: controlType });
    } else {
      this.props.onUpdate(path, { values: searchCriteria.values, control_type: controlType });
    }
  }

  makeLabels(minVal, maxVal) {
    const { searchCriteria } = this.props;
    const { filter } = searchCriteria;
    const metaType = (filter.meta || {}).type;

    let minLabel = null;
    let maxLabel = null;
    const hasMin = minVal !== undefined && minVal !== null;
    const hasMax = maxVal !== undefined && maxVal !== null;
    if (metaType === 'currency') {
      minLabel = hasMin ? (<MoneyDisplay value={minVal} precision={1} abbreviated />) : null;
      maxLabel = hasMax ? (<MoneyDisplay value={maxVal} precision={1} abbreviated />) : null;
    } else if (filter.tag === 'tenure') {
      minLabel = hasMin ? (<AgeDisplay value={minVal} isMonth />) : null;
      maxLabel = hasMax ? (<AgeDisplay value={maxVal} isMonth />) : null;
    } else if (filter.tag === 'opportunityAge' || filter.tag === 'dayPush') {
      minLabel = hasMin ? (<AgeDisplay value={minVal} />) : null;
      maxLabel = hasMax ? (<AgeDisplay value={maxVal} />) : null;
    } else if (filter.tag === 'pushFrequency') {
      minLabel = hasMin ? (<NumberDisplay value={minVal} precision={0} suffix="time" suffixIsUnit />) : null;
      maxLabel = hasMax ? (<NumberDisplay value={maxVal} precision={0} suffix="time" suffixIsUnit />) : null;
    } else if (metaType === 'int') {
      minLabel = hasMin ? (<NumberDisplay value={minVal} precision={0} />) : null;
      maxLabel = hasMax ? (<NumberDisplay value={maxVal} precision={0} />) : null;
    } else if (metaType === 'percent') {
      minLabel = hasMin ? (<NumberDisplay value={minVal} precision={1} suffix="%" />) : null;
      maxLabel = hasMax ? (<NumberDisplay value={maxVal} precision={1} suffix="%" />) : null;
    } else {
      minLabel = hasMin ? (<NumberDisplay value={minVal} precision={1} abbreviated />) : null;
      maxLabel = hasMax ? (<NumberDisplay value={maxVal} precision={1} abbreviated />) : null;
    }
    return [minLabel, maxLabel];
  }

  canUseSlider() {
    const { searchCriteria } = this.props;
    const { filter } = searchCriteria;
    const tree = filter.values;
    return tree && tree.length >= 3;
  }

  renderSlider(labels) {
    const { disabled, searchCriteria } = this.props;
    const { filter, values } = searchCriteria;
    const tree = filter.values;

    const amountMin = values[0];
    const amountMax = values[1];

    const [minAmountIndex, maxAmountIndex] = this.getMinMaxIndex(amountMin, amountMax);

    const minIndex = 0;
    const maxIndex = tree.length - 1;

    return (
      <div className={styles.slider}>
        <div className="clreafix">
          <div className="pull-left">{labels[0]}</div>
          <div className="pull-right">{labels[1]}</div>
        </div>
        <table>
          <tbody>
            <tr>
              <td className={styles.sliderControl}>
                <Range
                  disabled={disabled}
                  defaultValue={[minIndex, maxIndex]}
                  value={[minAmountIndex, maxAmountIndex]}
                  min={minIndex}
                  max={maxIndex}
                  onChange={(range) => {
                    this.onSliderChange([tree[range[0]], tree[range[1]]]);
                  }}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  renderMinMaxControls() {
    const { searchCriteria, disabled } = this.props;
    return (
      <MinMaxForm
        disabled={disabled}
        searchCriteria={searchCriteria}
        onUpdate={(min, max) => { this.onSliderChange([min, max]); }}
      />
    );
  }

  renderControls(labels) {
    const canUseSlider = this.canUseSlider();
    const isCustom = this.getIsUsingMinMaxControl();

    const panels = [];
    if (canUseSlider) {
      panels.push((
        <div key="slider" className={`panel panel-default ${styles.dateRangePanel}`}>
          <div className="panel-heading">
            <h3
              onClick={() => {
                this.forceCustom(false);
              }}
              className="panel-title clearfix"
            >
              <Fa name={isCustom ? 'caret-up' : 'caret-down'} className="pull-right" /> Select a predefined range
            </h3>
          </div>
          <Collapse in={!isCustom}>
            <div className="panel-body">
              {this.renderSlider(labels)}
            </div>
          </Collapse>
        </div>
      ));
    }

    panels.push((
      <div key="minmax" className={`panel panel-default ${styles.dateRangePanel}`}>
        <div className="panel-heading">
          {canUseSlider &&
            <h3
              onClick={() => {
                this.forceCustom(true);
              }}
              className="panel-title clearfix"
            >
              <Fa name={!isCustom ? 'caret-up' : 'caret-down'} className="pull-right" /> Enter a custom range
            </h3>
          }
          {!canUseSlider && <h3>Enter a custom range</h3>}
        </div>
        <Collapse in={isCustom}>
          <div className="panel-body">
            {this.renderMinMaxControls()}
          </div>
        </Collapse>
      </div>
    ));

    return panels;
  }

  render() {
    const { searchCriteria, readOnly } = this.props;
    const { filter, values } = searchCriteria;
    let { label } = filter;
    if (filter.meta && filter.meta.label) {
      ({ label } = filter.meta);
    }
    

    if (!this.canUseSlider() && !readOnly) {
      return this.renderMinMaxControls();
    }

    const labels = this.getMinMaxLabels();
    let comp = null;

    if (labels[0] && labels[1]) {
      const amountMin = values[0];
      const amountMax = values[1];
      if (amountMin === amountMax) {
        comp = (<span>{label} is <strong>{labels[0]}</strong></span>);
      } else {
        comp = (<span>{label} is between <strong>{labels[0]}</strong> and <strong>{labels[1]}</strong></span>);
      }
    } else if (labels[0]) {
      comp = (<span>{label} is starting from <strong>{labels[0]}</strong></span>);
    } else if (labels[1]) {
      comp = (<span>{label} is up to <strong>{labels[1]}</strong></span>);
    } else {
      comp = (<span>Select minimum and maximum value for <strong>{label}</strong></span>);
    }

    if (readOnly) {
      return (
        <div className={styles.sliderWrapper}>{comp}</div>
      );
    }

    return (
      <div className={styles.sliderWrapper}>
        <div>
          <a href="" onClick={this.toggleExpand}>{comp}</a>
        </div>
        <p />
        <Collapse in={this.state.expanded}>
          <div>
            {this.renderControls(labels)}
          </div>
        </Collapse>
      </div>
    );
  }
}
function mapStateToProps() {
  return {};
}

SliderField.propTypes = {
  disabled: PropTypes.bool,
  path: PropTypes.arrayOf(PropTypes.string).isRequired,
  searchCriteria: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    filter: PropTypes.shape({
      _id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      meta: PropTypes.shape({
        label: PropTypes.string,
      }),
    }),
    values: PropTypes.arrayOf(PropTypes.number),
    control_type: PropTypes.string,
  }),
  onUpdate: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};
SliderField.defaultProps = {
  searchCriteria: null,
  disabled: false,
  readOnly: false,
};

export default masterField(connect(mapStateToProps)(SliderField));
